import { createContext, useContext, useState, useEffect } from "react";

/**
 * 왼쪽 메뉴(LNB)의 폭 상태.
 *
 * **기본은 메뉴 이름까지 보이는 펼친 폭(290px)** 이다 (2026-10-02 사용자 결정: «LNB 는 마우스를
 * 올려야 보이는 것 말고 기본으로 항상 보이게»). 그 전엔 아이콘만 보이는 좁은 띠(90px)로 시작해
 * 마우스를 올려야 이름이 보였는데, 처음 쓰는 사람은 아이콘만으로 무슨 메뉴인지 알 수 없었다.
 * 접고 싶으면 **메뉴 상단의 ⚙️ 설정 → '메뉴 고정 해제'** — 그러면 예전처럼 좁은 띠가 되고, 마우스를
 * 올리면 그 자리에서 290px 로 펼쳐지되 본문을 밀지는 않는다(본문 위에 잠깐 겹쳐 뜬다). 접은 상태는
 * 저장하지 않는다 — 새로 들어오면 언제나 펼친 채로 시작한다.
 * (좁은 화면에서는 이 값과 무관하게 햄버거로 여는 서랍이다.)
 *
 * 2026-08-28: 메뉴를 화면 밖으로 통째로 미는 세로 손잡이(티켓 #508, `isHidden`)를 없앴다.
 * 기본이 이미 아이콘 폭이라 손잡이가 벌어 주던 200px 이 무의미해졌고, 손잡이가 차지하던
 * 50px 자리까지 본문에 돌려줬다. 계정별 저장(`app-lnb-hidden` · `pb.lnbHidden`)도 함께
 * 사라졌다 — 서버에 남은 개인설정 행은 아무도 읽지 않는 무해한 잔재다.
 *
 * 2026-08-31 에 선택 이용(외부용) 계정만 펼친 채로 시작하게 했던 예외는, 2026-10-02 모두가 펼친 채로
 * 시작하면서 필요 없어져 걷어냈다.
 */

type SidebarContextType = {
  isExpanded: boolean;
  isMobileOpen: boolean;
  isHovered: boolean;
  activeItem: string | null;
  openSubmenu: string | null;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  setIsHovered: (isHovered: boolean) => void;
  setActiveItem: (item: string | null) => void;
  toggleSubmenu: (item: string) => void;
  setIsMobileOpen: (open: boolean) => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // 기본은 펼침(메뉴 이름까지 보이는 폭). 저장하지 않는다 — 새로 들어오면 언제나 펼친 채로 시작한다.
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1280; // Mobile behavior up to xl breakpoint (1280px)
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleSidebar = () => {
    setIsExpanded((prev) => !prev);
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen((prev) => !prev);
  };

  // 지금 왼쪽 메뉴가 본문에서 차지하는 폭을 CSS 변수 --lnb-w 로 내보낸다 (티켓 #160).
  // 화면을 덮는 창(모달)이 메뉴를 비켜서 뜨게 하는 공통 규칙(.lnb-inset, src/index.css)이
  // 이 값을 읽는다. 리액트 트리 바깥(포털)로 나가는 모달까지 한 번에 따라오게 하려면
  // 컨텍스트 값이 아니라 문서 루트의 CSS 변수여야 한다.
  // 접힌 메뉴의 호버 미리보기는 본문 위에 잠깐 겹쳐 보일 뿐, 본문·모달을 밀어내지 않는다.
  const lnbWidth = isMobile ? 0 : isExpanded ? 290 : 90;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--lnb-w", `${lnbWidth}px`);
    // 메뉴가 없는 화면(공개 시세표·고객 입력폼 등)으로 넘어가면 0 으로 돌려놓는다.
    return () => {
      root.style.removeProperty("--lnb-w");
    };
  }, [lnbWidth]);

  const toggleSubmenu = (item: string) => {
    setOpenSubmenu((prev) => (prev === item ? null : item));
  };

  return (
    <SidebarContext.Provider
      value={{
        isExpanded: isMobile ? false : isExpanded,
        isMobileOpen,
        isHovered,
        activeItem,
        openSubmenu,
        toggleSidebar,
        toggleMobileSidebar,
        setIsHovered,
        setActiveItem,
        toggleSubmenu,
        setIsMobileOpen,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
